import type { ReactNode } from 'react';

/**
 * Standard page-title banner used at the top of every authenticated route.
 *
 * Renders the navy-gradient card with the page title and an optional
 * subtitle. The shape is identical across every page — title + body
 * copy — so the component owns the gradient, padding, shadow, typography,
 * and width. Routes only supply ``title`` and ``subtitle``.
 *
 * Width is fixed at 1280px (CLAUDE.md ``.wrap`` convention) so every
 * page header lines up across the app regardless of what the ``<main>``
 * below it does.
 *
 * ``subtitle`` is ``ReactNode`` so callers can embed accents (e.g. the
 * ``<span className="text-electric font-bold">*</span>`` marker on
 * forms with required fields) without re-creating the whole banner.
 */
export function PageHero({
  title,
  subtitle,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
}) {
  return (
    // Outer wrapper carries the responsive page GUTTER (matching the page
    // <main>, lg:px-10) so the blue card is inset from the viewport edges
    // and lines up with the content below — never full-bleed/edge-to-edge.
    <div className="max-w-[1280px] mx-auto mt-8 px-4 sm:px-6 lg:px-10">
      <section
        className="
          px-5 sm:px-7 lg:px-9 py-5 sm:py-[26px]
          bg-gradient-to-br from-[#1e3c72] to-[#2a5298]
          text-white rounded-xl
          shadow-hero
        "
      >
        {/* Titles are often FILENAMES, and a filename is one unbreakable token: without a
            break rule a long one widened the hero past the phone, which widened the page, and
            everything below then had room it does not really have — a sticky action bar set to
            wrap stopped wrapping and its last button was cut off. Break inside the word. */}
        <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight m-0 mb-1.5 [overflow-wrap:anywhere]">
          {title}
        </h1>
        {subtitle && (
          <p className="opacity-90 text-[14px] m-0 [overflow-wrap:anywhere]">{subtitle}</p>
        )}
      </section>
    </div>
  );
}
